import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Checkbox, Input, Table, InputNumber, Empty } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// NAVIGATION
import { useLocation } from "react-router-dom";
// REDUX
import { useSelector } from "react-redux";
import { useLazyGetBankQuery, usePutBankMutation } from "../../../redux/banks/bankAPI";
// UTILS
import { getTableRowClass } from "../../../utils/utils";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// COMPONENTS
import BanksFloorConfigurationHeader from "./BanksFloorConfigurationHeader";
import GlobalAlert2 from "../../home/GlobalAlert2";
import TableNoDataBox from "../../components/table/TableNoDataBox";
// INTERFACES
import { BankFloorInterface } from "../../../interfaces/EdgeManagement.interface";
import { LiftInterface } from "../../../interfaces/Lift.interface";
import { BankInterface } from "../../../interfaces/Bank.interface";
import { RootState } from "../../../app/store";
// CONSTANTS
import { BLANK_BANK } from "../../../constants/bank";
// FLATBUFFER DISPLAY HELPERS
import { formatUuidT } from "../../../flatbuffers/display";

function BanksFloorConfigurationTable() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const location = useLocation();
  const bankId = new URLSearchParams(location.search).get("idBank");
  const { Column, ColumnGroup } = Table;
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [bank, setBank] = useState<BankInterface>({ ...BLANK_BANK });
  const [errorMessage, setErrorMessage] = useState<any>({});

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [
    triggerGetBank,
    {
      data,
      isLoading: isLoadingGetBank,
      isFetching: isFetchingGetBank,
      isError: isErrorGetBank,
      error: errorGetBank,
    },
  ] = useLazyGetBankQuery();

  const [
    triggerPutBank,
    {
      isSuccess: isSuccessPutBank,
      isError: isErrorPutBank,
      error: errorPutBank,
      isLoading: isLoadingPutBank,
      reset: resetPutBank,
    },
  ] = usePutBankMutation();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getInitialData = async () => {
    const token = await GETJwtToken();
    triggerGetBank({
      bankId,
      token,
    });
  };

  const onClickSave = async () => {
    const token = await GETJwtToken();
    const { dataBlocks: _dataBlocks, ...copyFields } = bank as any;
    const body: any = {
      ...copyFields,
      lifts: [],
      lift_floors: bank.lifts,
    };
    const BODY = {
      id: formatUuidT(bank.id),
      body,
      token,
    };
    triggerPutBank(BODY);
  };

  const onChangeStartFloorNumber = (newValue: boolean) => {
    setBank((prev) => ({ ...prev, startFloorNo: newValue ? 1 : 0 }));
  };

  const onChangeInputTextValue = (newValue: any, bankFloorId: string, inputName: string) => {
    if ((bank as any).bankFloors) {
      const floors: any[] = (bank as any).bankFloors;
      const index = floors.findIndex((x: any) => x.id === bankFloorId);
      if (index >= 0) {
        const newFloors = floors.map((f: any, i: number) =>
          i === index ? { ...f, [inputName]: newValue } : f,
        );
        setBank((prev) => ({ ...prev, bankFloors: newFloors }));
      }
    }
  };

  const onChangeDoorLift = (
    newValue: any,
    liftId: string,
    bankFloorId: string,
    doorType: string,
  ) => {
    if (bank.lifts) {
      const newLifts = bank.lifts.map((lift: any) => {
        if (lift.id !== liftId) return lift;
        const newLiftFloors = (lift.liftFloors || []).map((lf: any) => {
          if (lf.bankFloorId !== bankFloorId) return lf;
          if (doorType === "front") return { ...lf, frontDoor: newValue.target.checked };
          if (doorType === "rear") return { ...lf, rearDoor: newValue.target.checked };
          return lf;
        });
        return { ...lift, liftFloors: newLiftFloors };
      });
      setBank((prev) => ({ ...prev, lifts: newLifts }));
    }
  };

  const onChangeDoorBank = (newValue: any, bankFloorId: string, doorType: string) => {
    const floors: any[] = (bank as any).bankFloors || [];
    const index = floors.findIndex((x: any) => x.id === bankFloorId);
    if (index >= 0) {
      const newFloors = floors.map((f: any, i: number) => {
        if (i !== index) return f;
        if (doorType === "front") return { ...f, frontDoor: newValue.target.checked };
        return { ...f, rearDoor: newValue.target.checked };
      });
      setBank((prev) => ({ ...prev, bankFloors: newFloors }));
    }
  };

  const checkInterminateCheckBox = (returnValue: string, object: any, valueName: string) => {
    // Extracting the values of the property
    const values = object.map((obj: any) => obj[valueName]);
    // Checking if all values are true
    const allTrue = values.every((value: any) => value === true);
    // Checking if all values are false
    const allFalse = values.every((value: any) => value === false);
    // Checking if there are mixed values
    const mixedValues = !allTrue && !allFalse;
    if (returnValue === "mixed") {
      return mixedValues;
    }
    if (allTrue) {
      return true;
    }
    return false;
  };

  const checkAllBoxes = (e: any, objectName: string, valueName: string, liftId: string) => {
    if (objectName === "bank_floors" && (bank as any).bankFloors) {
      const newFloors = ((bank as any).bankFloors as any[]).map((f: any) => ({
        ...f,
        [valueName]: e.target.checked,
      }));
      setBank((prev) => ({ ...prev, bankFloors: newFloors }));
    } else if (objectName === "lifts" && bank.lifts) {
      const newLifts = bank.lifts.map((lift: any) => {
        if (lift.id !== liftId) return lift;
        const newLiftFloors = (lift.liftFloors || []).map((lf: any) => ({
          ...lf,
          [valueName]: e.target.checked,
        }));
        return { ...lift, liftFloors: newLiftFloors };
      });
      setBank((prev) => ({ ...prev, lifts: newLifts }));
    }
  };

  const doorCheckedValue = (liftId: string, bankFloorId: string, doorType: string) => {
    if (bank.lifts) {
      const liftIndex = bank.lifts.findIndex((x: any) => x.id === liftId);
      if (liftIndex >= 0) {
        const floorIndex = bank.lifts[liftIndex].liftFloors.findIndex(
          (x: any) => x.bankFloorId === bankFloorId,
        );
        if (floorIndex >= 0) {
          if (doorType === "front") {
            return bank.lifts[liftIndex].liftFloors[floorIndex].frontDoor;
          }
          return bank.lifts[liftIndex].liftFloors[floorIndex].rearDoor;
        }
      }
    }
    return false;
  };

  const refreshPage = () => {
    getInitialData();
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    getInitialData();
    return () => {
      resetPutBank();
    };
  }, []);

  useEffect(() => {
    if (data && data.data) {
      const rawFloors: any[] = (data.data as any).bankFloors || [];
      const sortedByIndex = [...rawFloors].sort((a: any, b: any) => b.floorIndex - a.floorIndex);
      setBank({ ...(data.data as any), bankFloors: sortedByIndex });
    }
  }, [data]);

  useEffect(() => {
    if (errorGetBank) {
      setErrorMessage(errorGetBank);
    }
  }, [errorGetBank]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <div className="mb-2">
        <BanksFloorConfigurationHeader
          startFloorNumber={bank.startFloorNo === 1}
          bankName={String(bank.name ?? "")}
          onClickSave={onClickSave}
          onClickRefreshPage={refreshPage}
          onChangeStartFloorNumber={onChangeStartFloorNumber}
        />
      </div>
      <Table
        rowClassName={(record, index) => getTableRowClass(index, theme)}
        locale={{
          emptyText: (
            <>
              {isErrorGetBank ? (
                <TableNoDataBox
                  isError={isErrorGetBank}
                  errorMessage={
                    errorMessage && errorMessage.data && errorMessage.data.message
                      ? errorMessage.data.message
                      : "Error"
                  }
                  noItemsFound="Configuration not found"
                />
              ) : (
                <div className="generalStyles__flexCenter" style={{ height: "300px" }}>
                  <Empty description="Configuration not found" />
                </div>
              )}
            </>
          ),
        }}
        className="customTable"
        dataSource={(bank as any).bankFloors || []}
        rowKey={(record: any) => formatUuidT(record.id)}
        pagination={{
          defaultPageSize: 9999,
          showSizeChanger: false,
        }}
        size="small"
        loading={isLoadingGetBank || isLoadingPutBank || isFetchingGetBank}
      >
        <ColumnGroup>
          {/** FLOOR INDEX */}
          <Column
            title="Floor Index"
            dataIndex="floorIndex"
            key="floorIndex"
            filterSearch
            width="50px"
            render={(text, record: BankFloorInterface) => (
              <InputNumber
                placeholder={t("edgeManagement.banks.floorName")}
                value={text}
                onChange={(e) => {
                  onChangeInputTextValue(e, record.id, "floorIndex");
                }}
              />
            )}
          />
          {/** FLOOR NO */}
          <Column
            title="Floor No"
            dataIndex="floorNo"
            key="floorNo"
            filterSearch
            width="50px"
            render={(text, record: BankFloorInterface) => (
              <InputNumber
                placeholder={t("edgeManagement.banks.floorName")}
                value={text}
                onChange={(e) => {
                  onChangeInputTextValue(e, record.id, "floorNo");
                }}
              />
            )}
          />
          {/** POSITION */}
          <Column
            title="Position"
            dataIndex="position"
            key="position"
            filterSearch
            width="50px"
            render={(text, record: BankFloorInterface) => (
              <InputNumber
                placeholder={t("edgeManagement.banks.floorName")}
                value={text}
                onChange={(e) => {
                  onChangeInputTextValue(e, record.id, "position");
                }}
              />
            )}
          />
          {/** NAME */}
          <Column
            title={t("general.name")}
            dataIndex="name"
            key="name"
            className="generalStyles__tableHeaderLink"
            filterSearch
            width="50px"
            render={(text, record: BankFloorInterface) => (
              <div className="edgeManagement__floorNameName">
                <Input
                  placeholder={t("edgeManagement.banks.floorName")}
                  value={text}
                  onChange={(e) => {
                    onChangeInputTextValue(e.target.value, record.id, "name");
                  }}
                />
              </div>
            )}
          />
        </ColumnGroup>
        {/** BANK FLOORS */}
        <ColumnGroup title="Bank Floors">
          <Column
            title={
              <div>
                <span className="generalStyles__mrFix">F</span>
                <span>
                  <Checkbox
                    indeterminate={checkInterminateCheckBox(
                      "mixed",
                      (bank as any).bankFloors || [],
                      "frontDoor",
                    )}
                    checked={checkInterminateCheckBox(
                      "all",
                      (bank as any).bankFloors || [],
                      "frontDoor",
                    )}
                    onChange={(e) => {
                      checkAllBoxes(e, "bank_floors", "frontDoor", "");
                    }}
                    className="edgeManagement__floorNameTootle"
                  />
                </span>
              </div>
            }
            align="center"
            width={70}
            render={(text, record: BankFloorInterface) => (
              <>
                <Checkbox
                  className="edgeManagement__floorNameTootle"
                  checked={record.frontDoor}
                  onChange={(e) => {
                    onChangeDoorBank(e, record.id, "front");
                  }}
                />
              </>
            )}
          />
          <Column
            title={
              <div>
                <span className="generalStyles__mrFix">R</span>
                <span>
                  <Checkbox
                    indeterminate={checkInterminateCheckBox(
                      "mixed",
                      (bank as any).bankFloors || [],
                      "rearDoor",
                    )}
                    checked={checkInterminateCheckBox(
                      "all",
                      (bank as any).bankFloors || [],
                      "rearDoor",
                    )}
                    onChange={(e) => {
                      checkAllBoxes(e, "bank_floors", "rearDoor", "");
                    }}
                    className="edgeManagement__floorNameTootle"
                  />
                </span>
              </div>
            }
            align="center"
            width={70}
            render={(text, record: BankFloorInterface) => (
              <>
                <Checkbox
                  className="edgeManagement__floorNameTootle"
                  checked={record.rearDoor}
                  onChange={(e) => {
                    onChangeDoorBank(e, record.id, "rear");
                  }}
                />
              </>
            )}
          />
        </ColumnGroup>
        {/** LIFTS */}
        {bank && bank.lifts && (
          <>
            {bank.lifts
              .slice() // Create a shallow copy to avoid mutating the original array
              .sort((a: any, b: any) => {
                // Compare values based on a specific property (e.g., lift.name)
                if (a.position < b.position) return -1;
                if (a.position > b.position) return 1;
                return 0;
              })
              .map((lift: LiftInterface) => (
                <ColumnGroup title={String(lift.name ?? "")} key={String(lift.id)}>
                  <Column
                    title={
                      <div>
                        <span className="generalStyles__mrFix">F</span>
                        <span>
                          <Checkbox
                            indeterminate={checkInterminateCheckBox(
                              "mixed",
                              lift.liftFloors,
                              "frontDoor",
                            )}
                            checked={checkInterminateCheckBox("all", lift.liftFloors, "frontDoor")}
                            onChange={(e) => {
                              checkAllBoxes(e, "lifts", "frontDoor", String(lift.id));
                            }}
                            className="edgeManagement__floorNameTootle"
                          />
                        </span>
                      </div>
                    }
                    align="center"
                    width={100}
                    render={(text, record: BankFloorInterface) => (
                      <>
                        <Checkbox
                          className="edgeManagement__floorNameTootle"
                          checked={doorCheckedValue(String(lift.id), record.id, "front")}
                          onChange={(e) => {
                            onChangeDoorLift(e, String(lift.id), record.id, "front");
                          }}
                        />
                      </>
                    )}
                  />
                  <Column
                    title={
                      <div>
                        <span className="generalStyles__mrFix">R</span>
                        <span>
                          <Checkbox
                            indeterminate={checkInterminateCheckBox(
                              "mixed",
                              lift.liftFloors,
                              "rearDoor",
                            )}
                            checked={checkInterminateCheckBox("all", lift.liftFloors, "rearDoor")}
                            onChange={(e) => {
                              checkAllBoxes(e, "lifts", "rearDoor", String(lift.id));
                            }}
                            className="edgeManagement__floorNameTootle"
                          />
                        </span>
                      </div>
                    }
                    align="center"
                    width={100}
                    render={(text, record: BankFloorInterface) => (
                      <>
                        <Checkbox
                          className="edgeManagement__floorNameTootle"
                          checked={doorCheckedValue(String(lift.id), record.id, "rear")}
                          onChange={(e) => {
                            onChangeDoorLift(e, String(lift.id), record.id, "rear");
                          }}
                        />
                      </>
                    )}
                  />
                </ColumnGroup>
              ))}
          </>
        )}
      </Table>
      <GlobalAlert2
        isError={isErrorPutBank}
        isSuccess={isSuccessPutBank}
        requestType="PUT"
        error={errorPutBank}
        name="Bank"
      />
    </div>
  );
}

export default BanksFloorConfigurationTable;
